import { IDropdownOption, glyphs, getFormattedDateTimeByLocale } from "@treelab/panda";

import {
  ColumnType,
  ITableData,
  ITableColumn,
  FormulaResultType,
  ITypeOptions,
  ILookupTypeOptions,
  NumberFormat,
} from "../types/table-type";
import {
  IDimensionSelectOption,
  DimensionColumnPriority,
  CalculationType,
  StatisticsType,
  ContentSortType,
  TimeCycleType,
  AggregateFunctions,
  IChartFormValue,
  IAggregateFormValue,
} from "../types/form-type";
import { ChartType } from "../types/global-type";

import {
  DIMENSION_COLUMN_TYPE,
  VALUE_COLUMN_TYPE,
  SUMMARY_VALUE_COLUMN_PRIORITY_TYPE,
  IS_FORMULA_COLUMN_TYPE,
} from "../constants/tableData";

import { getCookie } from "../lib/cookies";
import { formatMessage } from "../lib/intl";

import { getOriginColumn, getRealColumn } from "./option_main";
import { formatValueNumberToString } from "./option_secondary";
import { isDateType } from "./tableData_secondary";

import {
  AGG_OPTION_COLUMN_TYPE,
  AGG_OPTION_LOOKUP_COLUMN_TYPE,
  AGG_OPTION_FORMULA_COLUMN_TYPE,
} from "../constants/aggregate";
import { FilterCondition } from "@treelab/common-logic";

const locale = getCookie("locale");

export const ICON_MAP: Partial<Record<ColumnType, { viewBox: string; data: JSX.Element }>> = {
  [ColumnType.TEXT]: glyphs.FIELD_TEXT,
  [ColumnType.PHONE]: glyphs.FIELD_PHONE,
  [ColumnType.EMAIL]: glyphs.FIELD_EMAIL,
  [ColumnType.CURRENCY]: glyphs.FIELD_CURRENCY,
  [ColumnType.COLLABORATOR]: glyphs.FIELD_COLLABORATOR,
  [ColumnType.MULTI_ATTACHMENT]: glyphs.FIELD_ATTACHMENT,
  [ColumnType.RECORD_REFERENCE]: glyphs.FIELD_RECORD_REF,
  [ColumnType.LONG_TEXT]: glyphs.FIELD_LONG_TEXT,
  [ColumnType.DATETIME]: glyphs.FIELD_DATE,
  [ColumnType.NUMBER]: glyphs.FIELD_NUMBER,
  [ColumnType.LOOKUP]: glyphs.FIELD_LOOKUP,
  [ColumnType.STATUS]: glyphs.FIELD_STATUS,
  [ColumnType.SELECT]: glyphs.FIELD_SELECT,
  [ColumnType.RATING]: glyphs.FIELD_RATING,
  [ColumnType.MULTI_SELECT]: glyphs.FIELD_MULTI_SELECT,
  [ColumnType.CHECKBOX]: glyphs.FIELD_CHECKBOX,
  [ColumnType.FORMULA]: glyphs.FIELD_FORMULA,
  [ColumnType.ROLLUP]: glyphs.FIELD_ROLLUP,
  [ColumnType.UNIQUE_ID]: glyphs.FIELD_UNIQUE_ID,
  [ColumnType.NOOP]: glyphs.FIELD_TEXT,
  [ColumnType.AUTO_NUMBER]: glyphs.FIELD_AUTO_NUMBER,
  [ColumnType.CREATED_AT]: glyphs.FIELD_CREATED_AT,
  [ColumnType.CREATED_BY]: glyphs.FIELD_CREATED_BY,
};

// 待国际化 value 计算方式 switc 的 options
export const statisticsTypeOption = [
  { labelId: "form.option.statisticsType.count", value: "COUNT" },
  { labelId: "form.option.statisticsType.statistic_column", value: "STATISTIC_COLUMN" },
];

export const calculationTypeOption: IDropdownOption[] = [
  {
    name: "form.option.calculationType.sum",
    value: CalculationType.SUM,
    params: { lableId: "form.option.calculationType.sum" },
  },
  {
    name: "form.option.calculationType.average",
    value: CalculationType.AVERAGE,
    params: { lableId: "form.option.calculationType.average" },
  },
  {
    name: "form.option.calculationType.max",
    value: CalculationType.MAX,
    params: { lableId: "form.option.calculationType.max" },
  },
  {
    name: "form.option.calculationType.min",
    value: CalculationType.MIN,
    params: { lableId: "form.option.calculationType.min" },
  },
];

const DIMENSION_COLUMN_PRIORITY_LEVEL: { [key in ColumnType]?: number } = {
  [ColumnType.SELECT]: 1,
  [ColumnType.STATUS]: 1,
  [ColumnType.DATETIME]: 2,
  [ColumnType.COLLABORATOR]: 3,
  [ColumnType.RATING]: 4,
  [ColumnType.MULTI_SELECT]: 5,
  [ColumnType.CREATED_BY]: 5,
  [ColumnType.CREATED_AT]: 5,
};

const DIMENSION_COLUMN_PRIORITY_LABEL_ID = {
  [DimensionColumnPriority.RECOMMEND]: "form.option.dimension.priority.1",
  [DimensionColumnPriority.OTHER]: "form.option.dimension.priority.2",
};

export const contentSortTypeOption: IDropdownOption[] = [
  { name: "form.option.contentSort.1", value: ContentSortType.X_FIRST_TO_LAST },
  { name: "form.option.contentSort.2", value: ContentSortType.X_LASTT_OF_IRST },
  { name: "form.option.contentSort.3", value: ContentSortType.Y_FIRST_TO_LAST },
  { name: "form.option.contentSort.4", value: ContentSortType.Y_LASTT_OF_IRST },
];

export const timeCycleOption = [
  { labelId: "form.option.time.cycle.day", value: TimeCycleType.DAY },
  { labelId: "form.option.time.cycle.week", value: TimeCycleType.WEEK },
  { labelId: "form.option.time.cycle.month", value: TimeCycleType.MONTH },
  { labelId: "form.option.time.cycle.quarter", value: TimeCycleType.QUARTER },
  { labelId: "form.option.time.cycle.year", value: TimeCycleType.YEAR },
];

// 获取 维度 默认值
export const getDimensionDefaultValue = (tableData: ITableData): string => {
  const { columns } = tableData;
  let dimensionId: string = "";
  let dimensionIdLevel: number = 1000;
  columns.forEach((item) => {
    const realType = getRealColumn(item.id, tableData)?.type;
    if (!!realType) {
      const currLevel = DIMENSION_COLUMN_PRIORITY_LEVEL[realType] || 100;
      if (currLevel < dimensionIdLevel) {
        dimensionId = item.id;
        dimensionIdLevel = currLevel;
      }
    }
  });
  return dimensionId;
};

// 获取 维度 option
export const getDimensionOptions = (tableData: ITableData): IDimensionSelectOption[] => {
  const result = tableData.columns
    .filter((item) => {
      const realColumnType = getRealColumn(item.id, tableData)?.type;
      if (!realColumnType) return false;
      return DIMENSION_COLUMN_TYPE[realColumnType];
    })
    .map((item) => {
      const realColumnType = getRealColumn(item.id, tableData)?.type || ColumnType.TEXT;
      const priorityLevel = DIMENSION_COLUMN_TYPE[realColumnType] as DimensionColumnPriority;
      return {
        name: item.name,
        value: item.id,
        priorityLevel: priorityLevel,
        priorityLevelLabelId: DIMENSION_COLUMN_PRIORITY_LABEL_ID[priorityLevel],
        priorityLevelNumber: DIMENSION_COLUMN_PRIORITY_LEVEL[realColumnType] || 6,
        icon: ICON_MAP[item.type],
      };
    })
    .sort((a, b) => a.priorityLevelNumber - b.priorityLevelNumber);
  return result;
};

// 获取 y轴 默认值
export const getYAxisDefaultValue = (tableData: ITableData): string => {
  const columns = tableData.columns;
  const result =
    columns.find((item) => {
      const type = getRealColumn(item.id, tableData)?.type;
      if (!type) return false;
      return VALUE_COLUMN_TYPE[type];
    })?.id || "";
  return result;
};

// 获取 y轴 option
export const getYAxisOptions = (tableData: ITableData): IDimensionSelectOption[] => {
  const result = tableData.columns
    .filter((item) => {
      const realColumnType = getRealColumn(item.id, tableData)?.type;
      if (!realColumnType) return false;
      return VALUE_COLUMN_TYPE[realColumnType];
    })
    .map((item) => ({
      name: item.name,
      value: item.id,
      priorityLevel: DimensionColumnPriority.RECOMMEND,
      priorityLevelNumber: 0,
      priorityLevelLabelId: DIMENSION_COLUMN_PRIORITY_LABEL_ID[DimensionColumnPriority.RECOMMEND],
      icon: ICON_MAP[item.type],
    }));
  return result;
};

// 统计 value 轴
export const getAggregateOptions = (tableData: ITableData): IDimensionSelectOption[] => {
  const { columns } = tableData;
  const result = columns
    .map((item) => {
      const columnType = getRealColumn(item.id, tableData)?.type;
      const priorityLevel =
        SUMMARY_VALUE_COLUMN_PRIORITY_TYPE[columnType || ColumnType.TEXT] || DimensionColumnPriority.OTHER;
      return {
        name: item.name,
        value: item.id,
        priorityLevel: priorityLevel,
        priorityLevelNumber: priorityLevel === DimensionColumnPriority.RECOMMEND ? 1 : 2,
        priorityLevelLabelId: DIMENSION_COLUMN_PRIORITY_LABEL_ID[priorityLevel],
        icon: ICON_MAP[item.type],
      };
    })
    .sort((a, b) => a.priorityLevelNumber - b.priorityLevelNumber);
  return result;
};

export const checkValueInArr = (value: any, arr: any[]): boolean => {
  return arr.includes(value);
};

const convertColumnByLookup = (column: ITableColumn<ColumnType.LOOKUP>) => {
  let newColumn = {
    ...column,
    typeOptions: column.typeOptions.lookupTypeOptions,
    type: column.typeOptions.lookupColumnType,
  };
  // 针对 lookup formula lookup rollup 进行二次转换
  if (IS_FORMULA_COLUMN_TYPE[newColumn.type]) {
    // @ts-ignore
    newColumn =
      newColumn.type === ColumnType.FORMULA
        ? convertColumnByFormula(newColumn as ITableColumn<ColumnType.FORMULA>)
        : convertColumnByRollup(newColumn as ITableColumn<ColumnType.ROLLUP>);
  }
  return newColumn;
};

const convertColumnByRollup = (column: ITableColumn<ColumnType.ROLLUP>) => {
  const { typeOptions } = column;
  const { resultType } = typeOptions;
  let newTypeOptions;
  switch (resultType) {
    case FormulaResultType.TEXT:
      newTypeOptions = {
        type: ColumnType.ROLLUP,
        realType: ColumnType.TEXT,
      };
      break;
    case FormulaResultType.NUMBER:
      if (typeOptions.numberFormat === NumberFormat.CURRENCY) {
        newTypeOptions = {
          type: ColumnType.ROLLUP,
          realType: ColumnType.CURRENCY,
          numberFormat: typeOptions.numberFormat,
          precision: typeOptions.precision,
          currencyFormat: typeOptions.currencyFormat,
        };
      } else {
        newTypeOptions = {
          type: ColumnType.ROLLUP,
          realType: ColumnType.NUMBER,
          numberFormat: typeOptions.numberFormat,
          precision: typeOptions.precision,
        };
      }
      break;
    case FormulaResultType.DATE:
      newTypeOptions = {
        type: ColumnType.ROLLUP,
        realType: ColumnType.DATETIME,
        dateFormat: typeOptions.dateFormat,
        timeFormat: typeOptions.timeFormat,
        includeTime: !!typeOptions.includeTime,
      };
      break;
    default:
      newTypeOptions = {
        realType: ColumnType.TEXT,
      };
      break;
  }
  const newColumn = {
    ...column,
    type: newTypeOptions.realType,
    typeOptions: newTypeOptions,
  };
  return newColumn;
};

const convertColumnByFormula = (column: ITableColumn<ColumnType.FORMULA>) => {
  const { typeOptions } = column;
  const { resultType } = typeOptions;
  let newTypeOptions;
  switch (resultType) {
    case FormulaResultType.TEXT:
      newTypeOptions = {
        type: ColumnType.FORMULA,
        realType: ColumnType.TEXT,
      };
      break;
    case FormulaResultType.NUMBER:
      if (typeOptions.numberFormat === NumberFormat.CURRENCY) {
        newTypeOptions = {
          type: ColumnType.FORMULA,
          realType: ColumnType.CURRENCY,
          numberFormat: typeOptions.numberFormat,
          precision: typeOptions.precision,
          currencyFormat: typeOptions.currencyFormat,
        };
      } else {
        newTypeOptions = {
          type: ColumnType.FORMULA,
          realType: ColumnType.NUMBER,
          numberFormat: typeOptions.numberFormat,
          precision: typeOptions.precision,
        };
      }
      break;
    case FormulaResultType.DATE:
      newTypeOptions = {
        type: ColumnType.FORMULA,
        realType: ColumnType.DATETIME,
        dateFormat: typeOptions.dateFormat,
        timeFormat: typeOptions.timeFormat,
        includeTime: !!typeOptions.includeTime,
      };
      break;
    default:
      newTypeOptions = {
        realType: ColumnType.TEXT,
      };
      break;
  }
  const newColumn = {
    ...column,
    type: newTypeOptions.realType,
    typeOptions: newTypeOptions,
  };
  return newColumn;
};

const convertFuncByColumnType: { [key in ColumnType]?: (column: ITableColumn) => any } = {
  [ColumnType.LOOKUP]: convertColumnByLookup,
  [ColumnType.ROLLUP]: convertColumnByRollup,
  [ColumnType.FORMULA]: convertColumnByFormula,
};

export const convertColumnObj = (column: ITableColumn): ITableColumn => {
  const convertFunc = convertFuncByColumnType[column.type];
  if (convertFunc) return convertFunc(column);
  return column;
};

export const formatAggregateValue = (
  value: number | string,
  calculationType: AggregateFunctions,
  typeOptions: ITypeOptions,
  columnType: ColumnType
): string => {
  switch (calculationType) {
    case AggregateFunctions.COUNT:
    case AggregateFunctions.EMPTY:
    case AggregateFunctions.FILLED:
    case AggregateFunctions.UNIQUE:
    case AggregateFunctions.CHECKED:
    case AggregateFunctions.UNCHECKED:
      return `${value}`;
    case AggregateFunctions.PERCENTAGE_EMPTY:
    case AggregateFunctions.PERCENTAGE_FILLED:
    case AggregateFunctions.PERCENTAGE_UNIQUE:
    case AggregateFunctions.PERCENTAGE_CHECKED:
    case AggregateFunctions.PERCENTAGE_UNCHECKED:
      return `${value}`;
    case AggregateFunctions.SUM:
    case AggregateFunctions.AVERAGE:
    case AggregateFunctions.MEDIAN:
    case AggregateFunctions.MIN:
    case AggregateFunctions.MAX:
    case AggregateFunctions.RANGE:
    case AggregateFunctions.STANDARD_DEVIATION:
      return formatValueNumberToString(value as number, { type: columnType, typeOptions } as ITableColumn);
    case AggregateFunctions.EARLIEST_DATE:
    case AggregateFunctions.LATEST_DATE:
      return getFormattedDateTimeByLocale(value as string, typeOptions as ITypeOptions<ColumnType.DATETIME>, locale);
    case AggregateFunctions.DATE_RANGE_DAYS:
      return `${value}${formatMessage({ id: "form.option.time.cycle.day" })}`;
    case AggregateFunctions.DATE_RANGE_MONTH:
      return `${value}${formatMessage({ id: "dropdown.aggregate.prefix.dateRangeMonth" })}`;
    default:
      return `${value}`;
  }
};

export const checkShowSplitSwitch = (formValue: IChartFormValue, tableData: ITableData): boolean => {
  const { chartType, dimensionId, isGroupAtCycle } = formValue;
  const dimensionColumn = getRealColumn(dimensionId, tableData);
  if (!dimensionColumn) return false;
  const { type } = dimensionColumn;
  if (
    chartType === ChartType.BASIC_LINE &&
    checkValueInArr(type, [
      ColumnType.DATETIME,
      ColumnType.NUMBER,
      ColumnType.RATING,
      ColumnType.CURRENCY,
      ColumnType.CREATED_AT,
    ])
  )
    return false;
  if (isDateType(type) && isGroupAtCycle) return false;
  const dimensionOriginalColumn = getOriginColumn(tableData, dimensionId);
  if (!dimensionOriginalColumn) return false;
  if (
    checkValueInArr(dimensionOriginalColumn.type, [
      ColumnType.COLLABORATOR,
      ColumnType.MULTI_SELECT,
      ColumnType.RECORD_REFERENCE,
      ColumnType.LOOKUP,
      ColumnType.ROLLUP,
      ColumnType.CREATED_BY,
    ])
  )
    return true;
  return false;
};

export const checkShowSortSelect = (formValue: IChartFormValue, tableData: ITableData): boolean => {
  const { chartType, dimensionId } = formValue;
  if (checkValueInArr(chartType, [ChartType.PIE_RING, ChartType.BASIC_PIE])) return false;
  const dimensionColumn = getRealColumn(dimensionId, tableData);
  if (!dimensionColumn) return true;
  const { type } = dimensionColumn;
  if (
    chartType === ChartType.BASIC_LINE &&
    checkValueInArr(type, [
      ColumnType.DATETIME,
      ColumnType.NUMBER,
      ColumnType.RATING,
      ColumnType.CURRENCY,
      ColumnType.CREATED_AT,
    ])
  )
    return false;
  return true;
};

// 增强对 formula rollup 的处理
export const getCalculationTypeOption = (
  formValue: IAggregateFormValue,
  tableData: ITableData
): { name: string; value: AggregateFunctions }[] => {
  const { calculationTargetId, statisticsType } = formValue;
  if (statisticsType === StatisticsType.COUNT) return [];
  const calculationColumn = getOriginColumn(tableData, calculationTargetId);
  if (!calculationColumn) return [];
  const columnType =
    calculationColumn.type === ColumnType.LOOKUP
      ? (calculationColumn.typeOptions as ILookupTypeOptions).lookupColumnType
      : calculationColumn.type;
  if (calculationColumn.type === ColumnType.LOOKUP) {
    const options =
      AGG_OPTION_LOOKUP_COLUMN_TYPE[(calculationColumn.typeOptions as ILookupTypeOptions).lookupColumnType];
    if (!!options) return options;
  }
  if (calculationColumn.type === ColumnType.FORMULA || calculationColumn.type === ColumnType.ROLLUP) {
    const realType = getRealColumn(calculationTargetId || "", tableData)?.type;
    return AGG_OPTION_FORMULA_COLUMN_TYPE[realType || ColumnType.TEXT] as { name: string; value: AggregateFunctions }[];
  }
  return AGG_OPTION_COLUMN_TYPE[columnType];
};


const FOR_MULTI_SELECT_MUTLI_OPTION: Partial<Record<FilterCondition, boolean>> = {
  [FilterCondition.EQUALS_ANY_OF]: true,
  [FilterCondition.EQUALS_NONE_OF]: true,
  [FilterCondition.HAS_ANY_OF]: true,
  [FilterCondition.HAS_ALL_OF]: true,
  [FilterCondition.HAS_NONE_OF]: true,
  [FilterCondition.EQUALS]: true,
};

const FOR_SELECT_SHOW_MULTI_OPTION: Partial<Record<FilterCondition, boolean>> = {
  [FilterCondition.EQUALS_ANY_OF]: true,
  [FilterCondition.EQUALS_NONE_OF]: true,
};

export const getIsMulti = (type: ColumnType, filterCondition: FilterCondition): boolean => {
  if (type === ColumnType.MULTI_SELECT || type === ColumnType.COLLABORATOR) {
    return FOR_MULTI_SELECT_MUTLI_OPTION[filterCondition] || false;
  }
  return FOR_SELECT_SHOW_MULTI_OPTION[filterCondition] || false;
};